import { useState, useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { generateNPC, ready } from '../lib/generator'
import type { GeneratorParams, GeneratorResult } from '../lib/generator'
import { mapCharacterRow, mapFamilyMembers, mapContacts, mapBiography, mapSkills, mapEquipmentItems, mapWeapons, mapArmour } from '../lib/generator/mapper'
import { generateAppearancePreview } from '../lib/generateAppearance'

export function useNPCGenerator(campaignId: string) {
  const queryClient = useQueryClient()
  const [isGenerating, setIsGenerating] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [result, setResult] = useState<GeneratorResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastParams, setLastParams] = useState<GeneratorParams | null>(null)

  useEffect(() => {
    ready.then(() => setIsReady(true))
  }, [])

  const generate = useCallback(async (params: GeneratorParams) => {
    setIsGenerating(true)
    setError(null)
    try {
      // Always ensure a seed for reproducibility
      const seed = params.seed ?? Math.floor(Math.random() * 2147483647)
      const seededParams = { ...params, seed }
      setLastParams(seededParams)

      // Single unified pipeline — constraints (requiredConditions, forbiddenConditions,
      // maritalStatus, requiredEvents, childrenAlive, childrenTotal) are all handled
      // inline by generateNPC.
      const npc = generateNPC(seededParams)
      if (npc) {
        const sum = ['STR','STA','DEX','AGL','EYE','HRG','SML','VOI','INT','AUR','WIL','CML']
          .map((k: string) => npc.attributes?.[k] ?? 0).reduce((a: number, b: number) => a + b, 0)
        console.log('[NPC Gen] Result:', npc.name, '| class:', npc.socialClass, '| attr sum:', sum, '| importance:', npc.importance ?? npc.aging?.importance)

        // AI appearance is generated after save, not during preview
      }
      setResult(npc)
    } catch (e) {
      console.error('[NPC Gen] Error:', e)
      setError(e instanceof Error ? e.message : 'Generation failed')
      setResult(null)
    } finally {
      setIsGenerating(false)
    }
  }, [])

  const save = useCallback(async () => {
    if (!result) return null
    setError(null)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data: campaign } = await supabase
        .from('campaigns')
        .select('current_tr_year')
        .eq('id', campaignId)
        .single()

      const gameYear = campaign?.current_tr_year ?? 720
      const charRow = mapCharacterRow(result, campaignId, gameYear, lastParams)

      const { data: character, error: charError } = await supabase
        .from('characters')
        .insert(charRow)
        .select()
        .single()

      if (charError) throw charError

      const familyMembers = mapFamilyMembers(result, character.id)
      const contacts = mapContacts(result, character.id)
      const biography = mapBiography(result, character.id)
      const skills = mapSkills(result, character.id)
      const equipmentItems = mapEquipmentItems(result, character.id)
      const weaponRows = mapWeapons(result, character.id)
      const armourRows = mapArmour(result, character.id)

      // Helper: run insert and return { label, error }
      const run = async (label: string, fn: () => PromiseLike<{ error: unknown }>) => {
        const res = await fn()
        return { label, error: res.error }
      }

      const ops = []

      if (familyMembers.length > 0) {
        ops.push(run('family_members', () => supabase.from('character_family_members').insert(familyMembers)))
      }
      if (contacts.length > 0) {
        ops.push(run('contacts', () => supabase.from('character_contacts').insert(contacts)))
      }
      if (biography.length > 0) {
        ops.push(run('biography', () => supabase.from('character_biography').insert(biography)))
      }
      if (skills.length > 0) {
        ops.push(run('skills', () => supabase.from('character_skills').insert(skills)))
      }
      if (weaponRows.length > 0) {
        ops.push(run('weapons', () => supabase.from('character_weapons').insert(weaponRows)))
      }
      if (armourRows.length > 0) {
        ops.push(run('armour', () => supabase.from('character_armour').insert(armourRows)))
      }
      if (equipmentItems.length > 0) {
        // Equipment uses two-pass insert for container nesting — wrapped in a single op
        ops.push((async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const containers = equipmentItems.filter((e: any) => !e._containerName)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const children = equipmentItems.filter((e: any) => e._containerName)

          if (containers.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const cleanRows = containers.map((r: any) => { const { _containerName, ...rest } = r; return rest })
            const res = await supabase.from('character_equipment').insert(cleanRows).select('id, item_name')
            if (res.error) return { label: 'equipment', error: res.error }

            if (children.length > 0 && res.data) {
              const containerRows = res.data as Array<{ id: string; item_name: string }>
              const findContainer = (name: string): string | null => {
                return containerRows.find(r => r.item_name === name)?.id
                  ?? containerRows.find(r => r.item_name.startsWith(name))?.id
                  ?? null
              }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const childRows = children.map((r: any) => {
                const { _containerName, ...rest } = r
                return { ...rest, parent_item_id: findContainer(_containerName) }
              })
              const childRes = await supabase.from('character_equipment').insert(childRows)
              if (childRes.error) return { label: 'equipment_children', error: childRes.error }
            }
          }
          return { label: 'equipment', error: null }
        })())
      }

      ops.push(run('snapshot', () => supabase.from('character_snapshots').insert({
        character_id: character.id,
        content_hash: `gen-${Date.now()}`,
        character_name: character.name,
        campaign_id: campaignId,
        character_role: 'npc',
        occupation_name: character.occupation_name,
        social_class: character.social_class,
        sex: character.sex ?? 'male',
        current_age: result.age ?? 30,
        funds_denarii: 0,
      })))

      const results = await Promise.all(ops)
      const errors = results
        .filter(r => r.error)
        .map(r => `${r.label}: ${(r.error as Error).message ?? JSON.stringify(r.error)}`)
      if (errors.length > 0) {
        console.error('Insert errors:', errors)
        throw new Error(`Failed to save: ${errors.join('; ')}`)
      }

      queryClient.invalidateQueries({ queryKey: ['campaign-characters', campaignId] })

      // Generate AI appearance after save (non-blocking)
      if (lastParams?.aiAppearance && result) {
        console.log('[NPC Gen] Generating AI appearance post-save...')
        generateAppearancePreview(buildAppearancePayload(result))
          .then(async (claudeAppearances) => {
            const appearances = { ...claudeAppearances, _source: 'claude', _generated_at: new Date().toISOString() }
            await supabase.from('characters').update({ appearances }).eq('id', character.id)
            queryClient.invalidateQueries({ queryKey: ['character', character.id] })
            console.log('[NPC Gen] AI appearance saved to character')
          })
          .catch(err => {
            console.warn('[NPC Gen] AI appearance failed:', err instanceof Error ? err.message : err)
          })
      }

      return character.id
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed'
      setError(msg)
      return null
    }
  }, [result, lastParams, campaignId, queryClient])

  const clear = useCallback(() => {
    setResult(null)
    setError(null)
  }, [])

  return { generate, save, clear, isGenerating, isReady, result, error }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildAppearancePayload(npc: GeneratorResult): Record<string, unknown> {
  const aging = (npc.aging ?? npc) as Record<string, any>
  const attrs = npc.attributes ?? aging.attributes ?? {}
  const pp = aging.physicalProfile ?? npc.physicalProfile ?? {}
  const eq = npc.equipment ?? aging.equipment ?? {}
  const history: Array<Record<string, any>> = npc.history ?? aging.history ?? []

  // Pick 3-5 biography highlights — major events or high-OP events
  const highlights = history
    .filter((h: any) => h.isMajor || (h.opsEarned ?? 0) >= 3 || h.eventId === 'conscripted' || h.eventId === 'married')
    .slice(0, 5)
    .map((h: any) => `Age ${h.age}: ${h.eventLabel ?? h.eventId}`)

  return {
    name: npc.name ?? aging.name ?? 'Unknown',
    age: npc.age ?? aging.age ?? 30,
    sex: npc.sex ?? aging.sex ?? 'male',
    socialClass: npc.socialClass ?? aging.socialClass ?? 'unguilded',
    occupation: typeof npc.occupation === 'string' ? npc.occupation
      : (npc.occupation as any)?.label ?? aging.occupation ?? 'laborer',
    culture: aging.culture ?? 'kaldor',
    physicalProfile: {
      heightInches: pp.heightInches ?? attrs._heightIn,
      frame: pp.frame ?? attrs._frame,
      weightLbs: pp.weightLbs ?? attrs._weightLbs,
      complexion: pp.complexion,
      hairColour: pp.hairColour,
      eyeColour: pp.eyeColour,
      hairLength: pp.hairLength,
      marks: pp.marks ?? [],
      apparentAge: pp.apparentAge,
      appearance: pp.appearance,
    },
    attributes: {
      STR: attrs.STR, STA: attrs.STA, DEX: attrs.DEX, AGL: attrs.AGL,
      EYE: attrs.EYE, HRG: attrs.HRG, SML: attrs.SML, VOI: attrs.VOI,
      INT: attrs.INT, AUR: attrs.AUR, WIL: attrs.WIL, CML: attrs.CML,
    },
    oceanScores: npc.oceanScores ?? aging.oceanScores,
    archetype: npc.archetype ?? aging.archetype,
    wealthLevel: (npc as any).wealthLevel ?? aging.wealthLevel,
    moralityBand: npc.moralityBand ?? aging.moralityBand,
    conditions: npc.conditions ?? aging.conditions ?? [],
    equipment: {
      loadouts: eq.loadouts,
      combat_gear: eq.combat_gear ?? (npc as any).combat_gear,
      jewellery: eq.jewellery ?? (npc as any).jewellery,
      profession_tools: eq.profession_tools,
    },
    mounts: eq.mounts ?? (npc as any).mounts,
    dogs: eq.dogs ?? (npc as any).dogs,
    birds: eq.birds ?? (npc as any).birds,
    biography_highlights: highlights,
  }
}
